import React, { useState } from 'react'
import { Panel } from 'primereact/panel'
import { Tag } from 'primereact/tag'
import { Button } from 'primereact/button'

import { useOcppCommandUpdateConfigKeysMutation, ChargePoint } from '../../../common/redux/api/web-api'
import { IConfigKey } from '../../../lib/loadOcppConfigKeys'
import setOcppConfigValue from '../../../lib/setOcppConfigValue'
import useText from '../../../common/hooks/useText'
import { Input } from '../../../common/uikit/Input'

export type ChargePointOcppConfigWidgetProps = {
    chargePoint: ChargePoint
}

const INSTALLATION_CHANGE_KEY = 'InstallationChange'

export function ChargePointOcppConfigWidget({ chargePoint }: ChargePointOcppConfigWidgetProps): React.ReactElement {
    const text = useText()

    const isOnline = chargePoint.chargePointStatus !== 'Offline'

    const [configKeys, setConfigKeys] = useState<IConfigKey[]>(chargePoint.ocppConfigKeys)

    const [saveConfigKeys, { isLoading: isConfigsLoading }] = useOcppCommandUpdateConfigKeysMutation()

    const changeConfigValue = (key: string, value: string) => {
        const updatedConfigKeys = setOcppConfigValue(key, value, configKeys)
        if (!updatedConfigKeys) return
        setConfigKeys(updatedConfigKeys)
    }

    const isConfigReadonly = (config: IConfigKey) => {
        if (!isOnline) {
            return true
        }

        if (config.readonly) {
            return true
        }

        if (config.key === INSTALLATION_CHANGE_KEY) {
            return true
        }

        return false
    }

    const saveConfigs = () => {
        saveConfigKeys({ chargePointId: chargePoint.id, body: configKeys })
    }
    return (
        <Panel
            header={
                <>
                    {text('lblOcppConfigWidget')}
                    {isConfigsLoading && <i className="pi pi-spin pi-spinner ml-1"></i>}
                </>
            }
            data-testid="ocpp-config-widget"
        >
            <div>
                {configKeys && configKeys.length && (
                    <div>
                        {!isOnline && (
                            <Tag
                                className="text-base mb-2 ml-2"
                                severity="warning"
                                value={text('msgCannotModifyOcppKeysBecauseChargerIsOffline')}
                                icon={<i className="pi pi-info-circle mr-2"></i>}
                            ></Tag>
                        )}
                        <div className="ml-2 mr-2">
                            {configKeys.map((config) => (
                                <div className="mb-2" key={config.key}>
                                    <Input
                                        label={config.key}
                                        data-testid="config-key"
                                        type="text"
                                        id={config.key}
                                        defaultValue={config.key === INSTALLATION_CHANGE_KEY ? '' : config.value}
                                        onChange={(event) => {
                                            changeConfigValue(config.key, event.target.value)
                                        }}
                                        disabled={isConfigReadonly(config)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="flex justify-content-end mt-2 mr-2">
                    <Button
                        onClick={saveConfigs}
                        disabled={!isOnline}
                        label={text('btnSave')}
                        data-testid="edit-close"
                    ></Button>
                </div>
            </div>
        </Panel>
    )
}
